// Shared service-center ALB (stacks/platform/service-center) that fronts
// ignition-backend and dmv. Its Route53 record drops the stage segment in prod
// — `service-center.axle.insure`, not `service-center.api.axle.insure` — so
// this cannot reuse the `prod -> api` mapping the legacy per-service API
// Gateway domains use. Every other stage keeps its own subdomain.
//
// REACT_APP_SERVICE_CENTER_BASE_URL overrides the host; only docker-compose
// sets it, pointing at the local nginx router. Duplicates
// serviceCenterBaseUrl in libs/core because CRA cannot resolve Nx path
// aliases — the two are pinned to the same table by their unit tests.
const stage = process.env.REACT_APP_STAGE || "dev";
export const SERVICE_CENTER_BASE_URL =
  process.env.REACT_APP_SERVICE_CENTER_BASE_URL ||
  `https://service-center${stage === "prod" ? "" : `.${stage}`}.axle.insure`;

export const genericClientLogoUrl =
  "https://axle-labs-assets.s3.amazonaws.com/clientLogos/generic.svg";

// 40 megapixels is the max image size limit for Google Document AI
export const MAX_MEGAPIXELS = 40;

// 5MB is the max file size limit for Anthropic's API. However, we need to reduce the max file size to 3.75MB to account for the ~33% increase in file size due to base64 encoding.
export const MAX_FILE_SIZE = 3.75;

// Max number of images that can be uploaded for Enhanced Manual
export const MAX_NUMBER_OF_IMAGES_TO_UPLOAD = 5;

// hitlAi caps each /direct-link poll at 20s and returns `started` while the
// phase is still running. Re-polling this many times keeps the ~5min ceiling
// those phases had when a single call did the whole wait.
export const MAX_HITL_STARTED_RETRIES = 15;

export const basePermissions: {
  order: number;
  name: string;
  icon: string;
  value: string;
}[] = [
    {
      order: 1,
      name: "Policy status",
      icon: "checkmark",
      value: "isActive",
    },
    {
      order: 2,
      name: "Policy number",
      icon: "policyNo",
      value: "policyNumber",
    },
    {
      order: 3,
      name: "Policy term",
      icon: "calendar",
      value: "expirationDate",
    },
    {
      order: 4,
      name: "Insureds",
      icon: "profile",
      value: "insureds",
    },
    {
      order: 5,
      name: "Coverages",
      icon: "shield",
      value: "coverages",
    },
    {
      order: 7,
      name: "Documents",
      icon: "document",
      value: "documents",
    },
    {
      order: 8,
      name: "Premium",
      icon: "price",
      value: "premium",
    },
  ];

export const returningUserEnums = [
  "re-link",
  "new-account",
  "new-user",
] as const;

export type ReturningUserEnum = (typeof returningUserEnums)[number];

export const MAP_FILE_EXT_TO_MIME = {
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
};

export type PolicyType = "auto" | "home" | "condo" | "flood" | "renters";

export const DOC_AI_UNSUPPORTED_POLICY_TYPES: PolicyType[] = ["flood", "renters"];

/* * ================================================================
 * Autopilot Error Messages
 * ==================================================================
 */
export const AUTOPILOT_ERROR_MESSAGE_ENUMERATION = [
  // 400
  "document-missing-fields",
  "document-type-not-supported-by-client",
  "document-type-not-supported-by-axle",
  "generic-document-error",
  "option-not-supported",
  "text-validation-failed",
  "unsupported-document",

  // 500
  "analyze-document-failed",
  "chunking-failed",
  "document-splitting-failed",
  "fail-to-parse-llm-message",
  "failed-to-fetch-document",
  "llm-request-failed",
  "text-extraction-failed",
] as const;

export type AutopilotErrorType =
  (typeof AUTOPILOT_ERROR_MESSAGE_ENUMERATION)[number];

export const AUTOPILOT_ERROR_MESSAGES_TABLE: Record<
  AutopilotErrorType,
  { code: number; message: string }
> = {
  // 400
  "document-missing-fields": {
    code: 400,
    message: "The document is missing required data fields",
    // "The provided document is missing required data. Please ensure that you've uploaded a complete document, including all necessary fields.",
  },
  "document-type-not-supported-by-client": {
    code: 400,
    message: "This document type is not supported by the current client",
    // "This document type is unsupported by the current client. Please upload an image or pdf document of the correct type with your policy information clearly legible.",
  },
  "document-type-not-supported-by-axle": {
    code: 400,
    message: "This document type is not currently supported by Document AI",
    // "This document type is unsupported by Document AI. Please upload an image or pdf document of the correct type with your policy information clearly legible.",
  },
  "generic-document-error": {
    code: 400,
    message:
      "Please make sure to upload an image or pdf document with your policy information clearly legible.",
  },
  "option-not-supported": {
    code: 400,
    message: "Unsupported service option provided",
  },
  "text-validation-failed": {
    code: 400,
    message:
      "The extracted text does not appear to be a valid insurance document",
    // "Document is either not an insurance document or is illegible. Please ensure that you upload a complete document with all policy information clearly legible.",
  },
  "unsupported-document": {
    code: 400,
    message:
      "Please make sure to upload an image or pdf document with your policy information clearly legible.",
  },

  // 500
  "analyze-document-failed": {
    code: 500,
    message: "Failed to analyze the document",
  },
  "chunking-failed": {
    code: 500,
    message: "Chunking failed",
  },
  "document-splitting-failed": {
    code: 500,
    message: "Failed to split and classify the document",
  },
  "fail-to-parse-llm-message": {
    code: 500,
    message: "Failed to parse LLM message",
  },
  "failed-to-fetch-document": {
    code: 500,
    message: "Failed to fetch document",
  },
  "llm-request-failed": {
    code: 500,
    message: "LLM request failed",
  },
  "text-extraction-failed": {
    code: 500,
    message: "Text extraction failed",
  },
};

export const RETRYABLE_UPLOAD_HTTP_CODES = new Set([408, 429, 500, 502, 503, 504]);